import { useEffect, useRef, useState } from 'react';
import { Loader2, Users } from 'lucide-react';
import { dashboardService } from '../../../services';
import PublisherRow from './PublisherRow';
import './UserNewsStats.css';

const UserNewsStats = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState(null);
  const [summary, setSummary] = useState(null);

  const pageRef = useRef(page);
  const loadingMoreRef = useRef(loadingMore);
  const paginationRef = useRef(pagination);
  const scrollRef = useRef(null);
  const fetchInProgressRef = useRef(false);

  const limit = 10;

  useEffect(() => {
    fetchStats(1, false);
  }, []);

  // Infinite scroll within this card's own scrollable content area.
  useEffect(() => {
    const scrollEl = scrollRef.current;
    if (!scrollEl) return;

    const handleScroll = () => {
      if (loadingMoreRef.current || !paginationRef.current?.hasNextPage) return;

      const { scrollTop, clientHeight, scrollHeight } = scrollEl;
      if (scrollTop + clientHeight >= scrollHeight - 150) {
        loadingMoreRef.current = true;
        const nextPage = pageRef.current + 1;
        setPage(nextPage);
        fetchStats(nextPage, true);
      }
    };

    scrollEl.addEventListener('scroll', handleScroll);
    return () => scrollEl.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => { pageRef.current = page; }, [page]);
  useEffect(() => { loadingMoreRef.current = loadingMore; }, [loadingMore]);
  useEffect(() => { paginationRef.current = pagination; }, [pagination]);

  const fetchStats = async (pageNum, append) => {
    if (fetchInProgressRef.current) return;
    fetchInProgressRef.current = true;

    if (append) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }
    setError(null);
    try {
      const response = await dashboardService.getUserNewsStats({ page: pageNum, limit });
      const newUsers = Array.isArray(response.data) ? response.data : [];
      setUsers((prev) => {
        if (!append) return newUsers;
        const existingIds = new Set(prev.map((u) => u.userId));
        return [...prev, ...newUsers.filter((u) => !existingIds.has(u.userId))];
      });
      setPagination(response.pagination);
      if (response.summary) {
        setSummary(response.summary);
      }
    } catch (err) {
      setError('Failed to load user news stats');
      console.error('User news stats error:', err);
    } finally {
      fetchInProgressRef.current = false;
      if (append) {
        setLoadingMore(false);
      } else {
        setLoading(false);
      }
    }
  };

  return (
    <div className="dashboard-card">
      <div className="card-header uns-header">
        <div className="uns-header-top">
          <h2 className="card-title">Publishers Data</h2>
        </div>
        {summary && (
          <div className="uns-summary-bar">
            <div className="uns-summary-item">
              <span className="uns-summary-label">Total Created:</span>
              <span className="uns-summary-value">{summary.totalCreated}</span>
            </div>
            <div className="uns-summary-item">
              <span className="uns-summary-label">Total Live:</span>
              <span className="uns-summary-value">{summary.totalLive}</span>
            </div>
            <div className="uns-summary-item">
              <span className="uns-summary-label">Human Edits:</span>
              <span className="uns-summary-value">
                {summary.totalLive ? Math.round((summary.totalLiveEdited / summary.totalLive) * 100) : 0}%
              </span>
            </div>
          </div>
        )}
      </div>

      <div className="card-content uns-content" ref={scrollRef}>
        {loading && users.length === 0 ? (
          <div className="uns-loading">
            <Loader2 size={28} className="spin" />
          </div>
        ) : error && users.length === 0 ? (
          <div className="uns-error">
            <p>{error}</p>
            <button className="retry-button" onClick={() => fetchStats(1, false)}>Retry</button>
          </div>
        ) : users.length === 0 ? (
          <div className="uns-empty">
            <Users size={40} />
            <p>No user news data yet</p>
          </div>
        ) : (
          <>
            <table className="uns-table">
              <thead>
                <tr>
                  <th className="uns-th-left">Email</th>
                  <th>
                    <div className="uns-th-main">Live/Total Created</div>
                  </th>
                  <th>
                    <div className="uns-th-main">Edits before Live/Total Live</div>
                  </th>
                  <th>Last Created</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <PublisherRow u={u} key={u.userId} />
                ))}
              </tbody>
            </table>

            {loadingMore && (
              <div className="uns-loading-more">
                <Loader2 size={16} className="spin" />
                Loading more...
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default UserNewsStats;
